"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* =========================
   TYPES
========================= */

interface Picture {
  id: number;
  attributes: {
    url: string;
    name: string;
    alternativeText: string | null;
  };
}

interface HeroMasonryProps {
  data: {
    id: string;
    title: string;
    subtitle: string;
    images: {
      data: Picture[];
    };
    isLive: boolean;
    isReversed: boolean;
    date: string;
    categorySlug: string;
    useH1Tag?: boolean;
    slug: string;
  };
}

/* =========================
   COMPONENT
========================= */

const HeroMasonry = ({ data }: HeroMasonryProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const images = data.images?.data || [];

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  /* Auto-scroll */
  useEffect(() => {
    if (!isAutoScroll || images.length <= 1) return;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }

    scrollIntervalRef.current = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
    };
  }, [isAutoScroll, images.length]);

  // Reset auto-scroll after manual navigation
  useEffect(() => {
    if (!isAutoScroll) {
      const timeoutId = setTimeout(() => {
        setIsAutoScroll(true);
      }, 10000);

      return () => clearTimeout(timeoutId);
    }
  }, [isAutoScroll]);

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setIsAutoScroll(false);
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setIsAutoScroll(false);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
    setIsAutoScroll(false);
  };

  // Handle swipe gestures for mobile
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      // Swipe left
      goToNext();
    }

    if (touchStart - touchEnd < -50) {
      // Swipe right
      goToPrevious();
    }
  };

  if (!images.length) return null;

  return (
    <section className="relative w-full bg-white overflow-hidden">
      {/* SEO-safe title & subtitle */}
      {data.useH1Tag ? (
        <h1 className="sr-only">{data.title}</h1>
      ) : (
        <h2 className="sr-only">{data.title}</h2>
      )}
      <p className="sr-only">{data.subtitle}</p>

      {/* Main Slider Container */}
      <div className="relative w-full">
        {/* Images Container - Responsive aspect ratios */}
        <div
          className="relative w-full 
                     aspect-[9/16] sm:aspect-[4/5] md:aspect-[2/1] lg:aspect-[16/6] xl:aspect-[1920/600]
                     max-h-[90vh]"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {images.map((image, index) => (
            <div
              key={image.id}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                index === currentImageIndex
                  ? "opacity-100 z-10"
                  : "opacity-0 z-0"
              }`}
            >
              <Image
                src={image.attributes.url}
                alt={image.attributes.alternativeText || image.attributes.name}
                fill
                priority={index === 0}
                quality={isMobile ? 75 : 90} // Lower quality on mobile for performance
                sizes="(max-width: 640px) 100vw, 
                       (max-width: 768px) 100vw, 
                       (max-width: 1024px) 100vw, 
                       1920px"
                className="object-cover w-full h-full"
              />
            </div>
          ))}

          {/* Left Navigation Arrow - Hide on very small screens */}
          {images.length > 1 && !isMobile && (
            <button
              onClick={goToPrevious}
              aria-label="Previous slide"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20
                         bg-black/20 hover:bg-black/40
                         rounded-full p-2 sm:p-3 md:p-4
                         transition-all duration-300
                         hover:scale-110 active:scale-95
                         backdrop-blur-sm"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </button>
          )}

          {/* Right Navigation Arrow - Hide on very small screens */}
          {images.length > 1 && !isMobile && (
            <button
              onClick={goToNext}
              aria-label="Next slide"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20
                         bg-black/20 hover:bg-black/40
                         rounded-full p-2 sm:p-3 md:p-4
                         transition-all duration-300
                         hover:scale-110 active:scale-95
                         backdrop-blur-sm"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </button>
          )}

          {/* Mobile Touch Indicators - Show only on mobile */}
          {images.length > 1 && isMobile && (
            <div className="absolute inset-0 flex items-center justify-between z-10 px-4">
              <button
                onClick={goToPrevious}
                aria-label="Previous slide"
                className="p-2 bg-black/30 rounded-full backdrop-blur-sm"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={goToNext}
                aria-label="Next slide"
                className="p-2 bg-black/30 rounded-full backdrop-blur-sm"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>

        {/* Dot Navigation - Responsive positioning and sizing */}
        {images.length > 1 && (
          <div
            className={`absolute z-20 flex gap-1 sm:gap-2 ${
              isMobile
                ? "bottom-2 left-1/2 -translate-x-1/2"
                : "bottom-4 md:bottom-6 left-1/2 -translate-x-1/2"
            }`}
          >
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`transition-all duration-300 rounded-full ${
                  index === currentImageIndex
                    ? isMobile
                      ? "w-6 h-1.5 bg-black/60"
                      : "w-8 h-2 bg-black/60"
                    : isMobile
                    ? "w-1.5 h-1.5 bg-black/30 hover:bg-black/50"
                    : "w-2 h-2 bg-black/30 hover:bg-black/50"
                }`}
              />
            ))}
          </div>
        )}

        {/* Auto-scroll Indicator - Responsive positioning */}
        {images.length > 1 && (
          <div
            className={`absolute z-20 ${
              isMobile ? "top-2 right-2" : "top-3 sm:top-4 right-3 sm:right-4"
            }`}
          ></div>
        )}

        {/* Current Slide Counter - Optional */}
        {/* {images.length > 1 && (
          <div
            className={`absolute z-20 font-medium ${
              isMobile
                ? "top-2 left-2 text-xs"
                : "top-3 sm:top-4 left-3 sm:left-4 text-sm"
            } bg-black/50 text-white px-2 py-1 rounded-full backdrop-blur-sm`}
          >
            {currentImageIndex + 1} / {images.length}
          </div>
        )} */}
      </div>

      {/* Swipe Instruction for Mobile */}
    </section>
  );
};

export default HeroMasonry;
